/**
 * Edit Folder Modal
 */

import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Radio, Checkbox, message } from 'antd';
import { folderApi } from '../../services/resourceService';
import { ResourceFolder, ResourceScope } from '../../types/resource';

interface EditFolderModalProps {
  visible: boolean;
  folder: ResourceFolder | null;
  allFolders: ResourceFolder[];
  onCancel: () => void;
  onSuccess: () => void;
}

const EditFolderModal: React.FC<EditFolderModalProps> = ({
  visible,
  folder,
  allFolders,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visibleToAll, setVisibleToAll] = useState(true);

  useEffect(() => {
    if (visible && folder) {
      const isVisibleToAll = folder.visible_to_all !== false;
      setVisibleToAll(isVisibleToAll);
      
      form.setFieldsValue({
        name: folder.name,
        parent_id: folder.parent_id,
        visible_to_all: isVisibleToAll,
        visible_roles: folder.visible_roles || [],
      });
    }
  }, [visible, folder, form]);

  // Filter out current folder and its descendants to prevent circular reference
  const getAvailableParentFolders = () => {
    if (!folder) return allFolders;
    
    const descendants = new Set<number>();
    const findDescendants = (folderId: number) => {
      descendants.add(folderId);
      allFolders.forEach(f => {
        if (f.parent_id === folderId && !descendants.has(f.id)) {
          findDescendants(f.id);
        }
      });
    };
    
    findDescendants(folder.id);
    return allFolders.filter(f => !descendants.has(f.id));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await folderApi.update(folder!.id, {
        name: values.name,
        parent_id: values.parent_id,
        visible_to_all: values.visible_to_all,
        visible_roles: values.visible_to_all ? null : (values.visible_roles || []),
      });
      
      message.success('分组更新成功');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error(error.response?.data?.detail || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const availableFolders = getAvailableParentFolders();
  const isEnterprise = folder?.scope === ResourceScope.ENTERPRISE;

  return (
    <Modal
      title="编辑分组"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
      >
        <Form.Item
          label="分组名称"
          name="name"
          rules={[
            { required: true, message: '请输入分组名称' },
            { max: 50, message: '分组名称不能超过50个字符' },
          ]}
        >
          <Input placeholder="请输入分组名称" />
        </Form.Item>

        <Form.Item
          label="父分组"
          name="parent_id"
          tooltip="选择父分组可以创建多级目录结构"
        >
          <Select
            placeholder="选择父分组（不选则为顶级分组）"
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {availableFolders.map((f) => (
              <Select.Option key={f.id} value={f.id}>
                {f.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Only show permission settings for enterprise folders */}
        {isEnterprise && (
          <>
            <Form.Item
              name="visible_to_all"
              label="可见权限"
            >
              <Radio.Group onChange={(e) => setVisibleToAll(e.target.value)}>
                <Radio value={true}>全员可见</Radio>
                <Radio value={false}>指定角色</Radio>
              </Radio.Group>
            </Form.Item>

            {!visibleToAll && (
              <Form.Item
                name="visible_roles"
                label="可见角色"
                rules={[{ required: true, message: '请选择至少一个角色' }]}
              >
                <Checkbox.Group>
                  <Checkbox value="admin">管理员</Checkbox>
                  <Checkbox value="lawyer">律师</Checkbox>
                  <Checkbox value="finance">财务</Checkbox>
                  <Checkbox value="sales">销售</Checkbox>
                </Checkbox.Group>
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EditFolderModal;
